import { ContentServiceFlowStatus, RequestError } from './types'
import { useCallback, useEffect, useRef } from 'react'
import { getFlowStatus, isRequestError, isPermanentError } from './utility'
import { useAtomWithErrorNotification } from 'shared/ui/GlobalDialog'
import { flowListAtom } from './atoms'
import { useNavigateTo } from 'infrastructure/routing'

export function useStatusPolling({
  flowId,
  pollingIntervalMs = 1000,
  onFlowPoll,
}: {
  flowId?: string
  pollingIntervalMs?: number
  onFlowPoll?: (flowResults?: ContentServiceFlowStatus | RequestError) => void
}) {
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const onFlowPollRef = useRef(onFlowPoll)
  onFlowPollRef.current = onFlowPoll

  useEffect(() => {
    if (!flowId) {
      onFlowPollRef.current?.(undefined)
      return
    }

    const pollFlow = async () => {
      const currentFlowStatus = await getFlowStatus(flowId)
      // Stop polling when flow is completed
      if (
        !isRequestError(currentFlowStatus) &&
        currentFlowStatus.data.completed
      ) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
          intervalRef.current = null
        }
      }
      // Stop polling on 401 or 404 (permanent errors)
      if (isPermanentError(currentFlowStatus)) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
          intervalRef.current = null
        }
      }
      // Continue polling for other communication errors (temporary)
      onFlowPollRef.current?.(currentFlowStatus)
    }

    // Poll immediately, then at the specified interval
    pollFlow()
    intervalRef.current = setInterval(pollFlow, pollingIntervalMs)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [flowId, pollingIntervalMs])
}

export function useAllFlows() {
  return useAtomWithErrorNotification(
    { errorTitle: 'Error Getting All Flows' },
    flowListAtom,
  )
}

export function useNavigateToFlow() {
  const doNavigate = useNavigateTo()
  return useCallback(
    (flowId?: string) => {
      doNavigate(`/content_service/flow${flowId ? `/${flowId}` : ''}`)
    },
    [doNavigate],
  )
}
